/**
 * <hero-video>
 * Wraps a <video muted loop playsinline preload="none" poster> that ships with
 * no <source>. After window load (and an idle slot) it injects the sources from
 * data-sources (JSON: [{ src, type }]) and plays. Skipped entirely when the
 * visitor prefers reduced motion, has data saver on, or motion is disabled in
 * the theme. Fades in on canplay. One play/pause button, hidden until ready.
 * Pauses while off-screen; a manual pause sticks.
 */
if (!customElements.get('hero-video')) {
  class HeroVideo extends HTMLElement {
    constructor() {
      super();
      this.loaded = false;
      this.userPaused = false;
      this.onToggle = this.onToggle.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
    }

    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-hero-video-toggle]');
      if (!this.video || !this.shouldLoad()) return;

      this.toggle?.addEventListener('click', this.onToggle);

      const schedule = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => this.load(), { timeout: 2500 });
        } else {
          window.setTimeout(() => this.load(), 250);
        }
      };

      if (document.readyState === 'complete') {
        schedule();
      } else {
        window.addEventListener('load', schedule, { once: true });
      }
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.onToggle);
      this.observer?.disconnect();
    }

    shouldLoad() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      if (navigator.connection && navigator.connection.saveData === true) return false;
      if (document.documentElement.classList.contains('no-motion')) return false;
      return true;
    }

    load() {
      if (this.loaded || !this.isConnected) return;
      let sources = [];
      try {
        sources = JSON.parse(this.dataset.sources || '[]');
      } catch (e) {
        sources = [];
      }
      if (!Array.isArray(sources) || sources.length === 0) return;
      this.loaded = true;

      const video = this.video;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.setAttribute('playsinline', '');

      sources.forEach(({ src, type }) => {
        if (!src) return;
        const el = document.createElement('source');
        el.src = src;
        if (type) el.type = type;
        video.appendChild(el);
      });

      video.addEventListener('canplay', () => this.onReady(), { once: true });
      video.addEventListener('play', () => this.syncState(true));
      video.addEventListener('pause', () => this.syncState(false));
      video.addEventListener(
        'error',
        () => {
          this.classList.remove('is-ready');
          this.toggle?.setAttribute('hidden', '');
        },
        { once: true }
      );

      video.load();
      this.play();
    }

    onReady() {
      this.classList.add('is-ready');
      this.toggle?.removeAttribute('hidden');
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onVisibility, { threshold: 0.1 });
        this.observer.observe(this);
      }
    }

    onVisibility(entries) {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (!visible) {
        this.video.pause();
      } else if (!this.userPaused) {
        this.play();
      }
    }

    play() {
      const promise = this.video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => this.syncState(false));
      }
    }

    onToggle() {
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    syncState(playing) {
      this.classList.toggle('is-paused', !playing);
      if (!this.toggle) return;
      const label = playing ? this.dataset.labelPause : this.dataset.labelPlay;
      if (label) this.toggle.setAttribute('aria-label', label);
    }
  }

  customElements.define('hero-video', HeroVideo);
}
